import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAdminWorkflowBlockSettings,
  deleteAdminWorkflowBlockSettings,
  getAdminBlockDefaultSettings,
  getAdminBlocksWithSettings,
  getAdminWorkflowBlockSettings,
  getAdminWorkflowEmailSettings,
  getAdminWorkflowErpSettings,
  getAdminWorkflowFlow,
  triggerAdminWorkflowFetchEmails,
  updateAdminWorkflowBlockSettings,
  updateAdminWorkflowEmailSettings,
  updateAdminWorkflowErpSettings,
  updateAdminWorkflowFlow,
  type UpdateEmailSettingsPayload,
  type UpdateErpSettingsPayload,
} from '@/services/api'
import type { WorkflowFlow } from '@/types/admin'

const setupKey = (part: string, organizationId: string, workflowId: string) =>
  ['admin-workflow', part, organizationId, workflowId] as const

export function useWorkflowFlow(organizationId?: string, workflowId?: string) {
  return useQuery({
    queryKey: setupKey('flow', organizationId ?? '', workflowId ?? ''),
    queryFn: () => getAdminWorkflowFlow(organizationId!, workflowId!),
    enabled: !!organizationId && !!workflowId,
  })
}

export function useUpdateWorkflowFlow(organizationId: string, workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (flow: WorkflowFlow) => updateAdminWorkflowFlow(organizationId, workflowId, flow),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: setupKey('flow', organizationId, workflowId) })
    },
  })
}

export function useWorkflowEmailSettings(organizationId?: string, workflowId?: string) {
  return useQuery({
    queryKey: setupKey('email-settings', organizationId ?? '', workflowId ?? ''),
    queryFn: () => getAdminWorkflowEmailSettings(organizationId!, workflowId!),
    enabled: !!organizationId && !!workflowId,
  })
}

export function useUpdateWorkflowEmailSettings(organizationId: string, workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateEmailSettingsPayload) =>
      updateAdminWorkflowEmailSettings(organizationId, workflowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: setupKey('email-settings', organizationId, workflowId),
      })
    },
  })
}

export function useWorkflowErpSettings(organizationId?: string, workflowId?: string) {
  return useQuery({
    queryKey: setupKey('erp-settings', organizationId ?? '', workflowId ?? ''),
    queryFn: () => getAdminWorkflowErpSettings(organizationId!, workflowId!),
    enabled: !!organizationId && !!workflowId,
  })
}

export function useUpdateWorkflowErpSettings(organizationId: string, workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateErpSettingsPayload) =>
      updateAdminWorkflowErpSettings(organizationId, workflowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: setupKey('erp-settings', organizationId, workflowId),
      })
    },
  })
}

export function useTriggerFetchEmails(organizationId: string, workflowId: string) {
  return useMutation({
    mutationFn: () => triggerAdminWorkflowFetchEmails(organizationId, workflowId),
  })
}

/** Block names the admin tool has a settings shape for — rarely changes, so a longer staleTime avoids refetching every time the dropdown opens. */
export function useBlocksWithSettings() {
  return useQuery({
    queryKey: ['admin-blocks-with-settings'],
    queryFn: getAdminBlocksWithSettings,
    staleTime: 5 * 60_000,
  })
}

export function useBlockDefaultSettings(blockName: string | null) {
  return useQuery({
    queryKey: ['admin-block-default-settings', blockName],
    queryFn: () => getAdminBlockDefaultSettings(blockName!),
    enabled: !!blockName,
  })
}

export function useWorkflowBlockSettings(organizationId?: string, workflowId?: string) {
  return useQuery({
    queryKey: setupKey('block-settings', organizationId ?? '', workflowId ?? ''),
    queryFn: () => getAdminWorkflowBlockSettings(organizationId!, workflowId!),
    enabled: !!organizationId && !!workflowId,
  })
}

export function useCreateWorkflowBlockSettings(organizationId: string, workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { block_name: string; settings: unknown }) =>
      createAdminWorkflowBlockSettings(organizationId, workflowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: setupKey('block-settings', organizationId, workflowId),
      })
    },
  })
}

export function useUpdateWorkflowBlockSettings(organizationId: string, workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ blockName, settings }: { blockName: string; settings: unknown }) =>
      updateAdminWorkflowBlockSettings(organizationId, workflowId, blockName, settings),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: setupKey('block-settings', organizationId, workflowId),
      })
    },
  })
}

export function useDeleteWorkflowBlockSettings(organizationId: string, workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (blockName: string) =>
      deleteAdminWorkflowBlockSettings(organizationId, workflowId, blockName),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: setupKey('block-settings', organizationId, workflowId),
      })
    },
  })
}
